import { useRef, useState } from 'react';
import { IconButton, Tooltip } from '@mui/material';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import userStateEngine2 from '../../controllers/UserState/UserStateEngine2';
import { UserStateKeys } from '../../controllers/UserState/UserStateKeys';

const DEFAULT_POS = { x: 16, y: 16 };

const loadPos = () => {
    try {
        const saved = userStateEngine2.getValue(UserStateKeys.contentFocusBtnPos);
        return saved ? JSON.parse(saved) : DEFAULT_POS;
    } catch {
        return DEFAULT_POS;
    }
};

/**
 * Floating draggable button shown in focus mode.
 * Click — exits focus mode.
 * Drag — repositions button, persists position to userState.
 * Disables iframe pointer events during drag to prevent event stealing.
 */
export default function FocusModeButton({ onExit }) {
    const [pos, setPos] = useState(loadPos);
    const posRef = useRef(pos);
    const dragging = useRef(false);
    const startMouse = useRef({ x: 0, y: 0 });
    const startPos = useRef({ x: 0, y: 0 });
    const moved = useRef(false);

    const handleMouseDown = (e) => {
        dragging.current = true;
        moved.current = false;
        startMouse.current = { x: e.clientX, y: e.clientY };
        startPos.current = posRef.current;

        // Disable iframe pointer events during drag — prevents iframe stealing mouse events
        const iframes = document.querySelectorAll('iframe');
        iframes.forEach((f) => (f.style.pointerEvents = 'none'));

        const handleMouseMove = (ev) => {
            if (!dragging.current) return;
            const dx = ev.clientX - startMouse.current.x;
            const dy = ev.clientY - startMouse.current.y;
            if (Math.abs(dx) > 3 || Math.abs(dy) > 3) moved.current = true;
            const next = { x: startPos.current.x + dx, y: startPos.current.y + dy };
            posRef.current = next;
            setPos(next);
        };

        const handleMouseUp = () => {
            dragging.current = false;

            // Re-enable iframe pointer events
            iframes.forEach((f) => (f.style.pointerEvents = ''));

            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);

            if (moved.current) {
                userStateEngine2.set([{ name: UserStateKeys.contentFocusBtnPos, value: JSON.stringify(posRef.current) }]);
            }
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    };

    const handleClick = () => {
        if (!moved.current) onExit();
    };

    return (
        <Tooltip title="Exit focus mode">
            <IconButton
                onMouseDown={handleMouseDown}
                onClick={handleClick}
                size="small"
                sx={{
                    position: 'absolute',
                    left: pos.x,
                    top: pos.y,
                    zIndex: 10,
                    bgcolor: '#0d1f33cc',
                    border: '0.5px solid #294766',
                    color: '#a3d1ff',
                    cursor: 'grab',
                    '&:hover': { bgcolor: '#1a2a40', color: '#66b2ff' },
                    '&:active': { cursor: 'grabbing' },
                }}
            >
                <FullscreenExitIcon fontSize="small" />
            </IconButton>
        </Tooltip>
    );
}
